import { Table, TableBody, TableRow, Typography } from "@mui/material"
import { Fragment, ReactElement } from "react"

import StackedKeyValueList from "./stackedKeyValueList"
import { BorderlessCell } from "./verticalKeyValueList"
import { useScreenSize } from "../../hooks/useScreenSize"
import { displayText } from "../../utils/general/generalUtils"

type KeyValueType = {
    [key: string]: string | number | boolean | undefined | null
}

export type NestedKeyValueListType = {
    [key: string]: KeyValueType
}

type NestedVerticalKeyValueListProps = {
    nestedKwargs: NestedKeyValueListType
    headerVairant?: "h5" | "h6"
}

/**
 * Renders nested key value pair list in table, broken up by headings
 * specified by nesting in `nestedKwargs`
 *
 * @example
 * <NestedVerticalKeyValueList nestedKwargs={{
 *     nestL1: {
 *         key1: "value1"
 *     }
 * }}
 * />
 */
const NestedVerticalKeyValueList = ({
    nestedKwargs,
    headerVairant,
}: NestedVerticalKeyValueListProps): ReactElement => {
    const { isMob } = useScreenSize()
    return (
        <Table>
            <TableBody>
                {Object.keys(nestedKwargs).map(
                    (nestLevel: string, index: number) => (
                        <Fragment key={nestLevel}>
                            <TableRow>
                                <BorderlessCell colSpan={2} sx={{ padding: 0 }}>
                                    <Typography
                                        marginBottom={2}
                                        marginTop={index === 0 ? 0 : 2}
                                        variant={headerVairant}
                                        fontWeight="500"
                                    >
                                        {nestLevel}
                                    </Typography>
                                </BorderlessCell>
                            </TableRow>
                            {isMob ? (
                                <StackedKeyValueList
                                    details={nestedKwargs[nestLevel]}
                                />
                            ) : (
                                Object.keys(nestedKwargs[nestLevel]).map(
                                    (key: string) => (
                                        <TableRow key={key}>
                                            <BorderlessCell>
                                                <Typography color="text.disabled">
                                                    {key}
                                                </Typography>
                                            </BorderlessCell>
                                            <BorderlessCell>
                                                <Typography>
                                                    {displayText(
                                                        nestedKwargs[nestLevel][
                                                            key
                                                        ]
                                                    )}
                                                </Typography>
                                            </BorderlessCell>
                                        </TableRow>
                                    )
                                )
                            )}
                        </Fragment>
                    )
                )}
            </TableBody>
        </Table>
    )
}

NestedVerticalKeyValueList.defaultProps = {
    headerVairant: "h5",
}

export default NestedVerticalKeyValueList
